/* ============================================================
   SAM-1074 — Business › Activity (canvas 21 oct, approved). The tenant-level twin of each business's
   Activity tab: Overview | Bookings | Passes. Overview has tiles (bookings, passes, waiting for approval,
   arriving next hour, checked in), a per-day chart, bookings by status and a table by business that opens
   that business's Activity. Bookings is the existing list (Bookings.tsx); Passes is the Passes list (Invitations) inside it.
   Only people who see every business get the tabs; a business-scoped user keeps the Bookings page.
   Numbers: per business, GET /bookings/stats, GET /business_access/stats and the upcoming booking rows
   (api/activityStats.ts, hooks/useActivityData.ts).
   ============================================================ */
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { worksAsFrontDesk } from '../../utils/frontDeskMatrix';
import { useBusinesses } from '../../hooks/useBusinessAccessType';
import { useActivityData } from '../../hooks/useActivityData';
import { toDayParam } from '../../api/businessAccessCombined';
import { downloadCSV } from '../../utils/csvExportUtils';
import { Bookings } from './Bookings';
import { Invitations } from '../invitation/Invitations';
import { TableContent } from '../LayoutPage';
import { DAY_WINDOWS, STATUS_FILTERS, activityCsv, nextHourLabel, windowLabel, type DayWindow } from './activityOverview';

type View = 'overview' | 'bookings' | 'passes';

const ink = (n: 900 | 700 | 500) => ({ color: `var(--ink-${n})` });
const brand = 'var(--tenant-primary, #005488)';
const selectBox = 'h-10 px-3 rounded-[8px] border border-solid border-[var(--line)] bg-white text-[14px] font-medium';
const card = 'bg-white rounded-[8px] min-w-0';
const cardShadow = { boxShadow: '0 1px 3px rgba(0,0,0,.1)' };

const Tile: React.FC<{ label: string; value: React.ReactNode; sub: string; warn?: boolean }> = ({ label, value, sub, warn }) => (
  <div className={`${card} px-5 py-[18px] flex flex-col gap-1.5`} style={cardShadow}>
    <span className="text-[13px] font-medium" style={ink(500)}>{label}</span>
    <span className="text-[30px] font-bold" style={{ color: warn ? 'var(--warn-fg)' : 'var(--ink-900)' }}>{value}</span>
    <span className="text-[13px]" style={ink(500)}>{sub}</span>
  </div>
);

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap" style={{ background: 'var(--warn-bg-2)', color: 'var(--warn-fg)' }}>{children}</span>
);

const num = (v: number | undefined | null): string => (v === undefined || v === null ? '—' : String(v));

const Overview: React.FC = () => {
  const now = useMemo(() => new Date(), []);
  const permissions = useUserPermissions();
  // The tenant Admin (group `admin`, not `xhinola_admin`) does not get the By business table; Xhinola staff do.
  const showByBusiness = !(permissions.isAdmin && !permissions.isXhinolaAdmin);
  const [businessId, setBusinessId] = useState('');
  const [win, setWin] = useState<DayWindow>('today');
  const [status, setStatus] = useState('');

  const { data: businessesData } = useBusinesses();
  const businesses = useMemo(() => {
    const raw = businessesData as unknown as Array<{ id: string; name: string }> | { data?: Array<{ id: string; name: string }> } | undefined;
    const list = Array.isArray(raw) ? raw : raw?.data ?? [];
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [businessesData]);
  const shown = useMemo(() => (businessId ? businesses.filter((b) => String(b.id) === businessId) : businesses), [businesses, businessId]);

  const { data, isLoading, updatedAt } = useActivityData({ businesses: shown, win, status, now, tenantWide: !businessId && businesses.length > 1 });
  const { rows, days, statuses } = data;
  const winName = DAY_WINDOWS.find((w) => w.k === win)!.label;
  const maxStatus = Math.max(1, ...statuses.map((s) => s.count));
  const maxDay = Math.max(1, ...days.map((d) => d.bookings + (d.passes ?? 0)));
  const updated = updatedAt ? `${Math.max(0, Math.round((Date.now() - updatedAt) / 60000))} min ago` : '';

  const exportCsv = () => downloadCSV(activityCsv(rows), `activity-${toDayParam(now)}.csv`);

  return (
    <div className="flex flex-col gap-5">
      <section className="flex items-end gap-2.5 flex-wrap" aria-label="Filters">
        <label className="flex flex-col gap-1 text-[12px] font-semibold" style={ink(500)}>
          Business
          <select aria-label="Business" value={businessId} onChange={(e) => setBusinessId(e.target.value)} className={`${selectBox} w-[190px]`}>
            <option value="">All businesses</option>
            {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-semibold" style={ink(500)}>
          Bookings in
          <select aria-label="Bookings in" value={win} onChange={(e) => setWin(e.target.value as DayWindow)} className={`${selectBox} w-[220px]`}>
            {DAY_WINDOWS.map((w) => <option key={w.k} value={w.k}>{windowLabel(w.k, now)}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-semibold" style={ink(500)}>
          Booking status
          <select aria-label="Booking status" value={status} onChange={(e) => setStatus(e.target.value)} className={`${selectBox} w-[160px]`}>
            <option value="">Any status</option>
            {STATUS_FILTERS.map((s) => <option key={s.id} value={s.param}>{s.label}</option>)}
          </select>
        </label>
        <button type="button" className="btn ghost ml-auto" onClick={exportCsv} disabled={rows.length === 0}><Download size={14} /> Export CSV</button>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3" aria-label="Totals">
        <Tile label="Bookings" value={isLoading ? '…' : data.bookings} sub={`${winName} · ${data.businessesWithBookings} ${data.businessesWithBookings === 1 ? 'business' : 'businesses'}`} />
        <Tile label="Passes" value={num(data.passes)} sub={`${winName} · ${num(data.checkedIn)} in`} />
        <Tile label="Waiting for approval" value={isLoading ? '…' : data.waiting} sub="Pending · next 7 days" warn={data.waiting > 0} />
        <Tile label="Arriving next hour" value={isLoading ? '…' : data.arriving} sub={nextHourLabel(now)} />
        <Tile label="Checked in" value={num(data.checkedIn)} sub={data.passes !== null ? `of ${data.passes} passes` : 'Passes inside now'} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4">
        <section className={`${card} p-5 flex flex-col gap-3.5`} style={cardShadow} aria-label="Per day">
          <div>
            <h2 className="m-0 text-[16px] font-semibold" style={ink(900)}>Per day</h2>
            <div className="text-[13px] mt-0.5" style={ink(500)}>Bookings and passes · next 7 days</div>
          </div>
          <div className="flex items-end gap-2.5 h-[180px]">
            {days.map((d, i) => {
              const passes = d.passes ?? 0;
              return (
                <div key={d.key} className="flex-1 flex flex-col items-center gap-1.5" title={`${d.label}: ${d.bookings} bookings, ${passes} passes`}>
                  <div className="w-full max-w-[44px] flex flex-col overflow-hidden rounded-t-[4px] justify-end" style={{ height: 150 }}>
                    <div style={{ height: `${(passes / maxDay) * 150}px`, background: '#9fc2bb' }} />
                    <div style={{ height: `${(d.bookings / maxDay) * 150}px`, background: '#0B5C4D' }} />
                  </div>
                  <span className="text-[12px]" style={{ ...ink(500), fontWeight: i === 0 ? 600 : 400 }}>{d.label}</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 text-[12px]" style={ink(700)}>
            <span><span className="inline-block w-2.5 h-2.5 rounded-[2px] mr-1.5" style={{ background: '#0B5C4D' }} />Bookings</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-[2px] mr-1.5" style={{ background: '#9fc2bb' }} />Passes</span>
          </div>
        </section>

        <section className={`${card} p-5 flex flex-col gap-3.5`} style={cardShadow} aria-label="By status">
          <div>
            <h2 className="m-0 text-[16px] font-semibold" style={ink(900)}>By status</h2>
            <div className="text-[13px] mt-0.5" style={ink(500)}>{DAY_WINDOWS.find((w) => w.k === win)!.label}</div>
          </div>
          <div className="flex flex-col gap-3.5">
            {statuses.map((s) => (
              <div key={s.id} className="flex items-center gap-3">
                <span className="w-[120px] text-[13px] font-semibold" style={ink(900)}>{s.label}</span>
                <div className="grow h-2.5 rounded-[5px]" style={{ background: 'var(--line-subtle)' }}>
                  <div className="h-2.5 rounded-[5px]" style={{ width: `${(s.count / maxStatus) * 100}%`, background: s.id === 1 ? '#d98a00' : s.id === 4 ? '#9CA3AF' : '#0B5C4D' }} />
                </div>
                <span className="w-6 text-right text-[13px] font-semibold" style={ink(900)}>{s.count}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {showByBusiness && (
      <section className={`${card} p-5 flex flex-col gap-3.5`} style={cardShadow} aria-label="By business">
        <div>
          <h2 className="m-0 text-[16px] font-semibold" style={ink(900)}>By business</h2>
          <div className="text-[13px] mt-0.5" style={ink(500)}>Pick a business to open its Activity tab.</div>
        </div>
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full border-collapse text-[14px]" style={ink(900)}>
            <thead>
              <tr className="text-left text-[12px] font-semibold" style={{ ...ink(500), borderBottom: '1px solid var(--line)' }}>
                {['Business', 'Bookings', 'Passes', 'Next 7 d', 'Pending', 'Checked in', 'Next'].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--line-subtle)' }}>
                  <td className="px-3 py-3"><Link to={`/business/${r.id}?tab=activity`} className="font-semibold no-underline" style={ink(900)}>{r.name}</Link></td>
                  <td className="px-3 py-3 font-semibold">{num(r.bookings)}</td>
                  <td className="px-3 py-3">{num(r.passes)}</td>
                  <td className="px-3 py-3">{num(r.nextSeven)}</td>
                  <td className="px-3 py-3">{r.pending ? <Pill>{r.pending}</Pill> : <span style={ink(500)}>—</span>}</td>
                  <td className="px-3 py-3">{num(r.checkedIn)}</td>
                  <td className="px-3 py-3 text-[13px]" style={ink(700)}>{r.next}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="m-0 p-0 list-none flex flex-col gap-2.5 md:hidden">
          {rows.map((r) => (
            <li key={r.id}>
              <Link to={`/business/${r.id}?tab=activity`} className="flex items-center gap-3 p-3 rounded-[8px] bg-white no-underline" style={{ ...cardShadow, ...ink(900) }}>
                <span className="grow min-w-0">
                  <div className="text-[15px] font-semibold truncate">{r.name}</div>
                  <div className="text-[12px]" style={ink(500)}>Next {r.next}</div>
                </span>
                <span className="text-right">
                  <div className="text-[16px] font-bold">{num(r.bookings)}</div>
                  {(r.pending ?? 0) > 0 && <Pill>{r.pending} pending</Pill>}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        {rows.length === 0 && <div className="text-[13px]" style={ink(500)}>No businesses to show.</div>}
        {updated && <div className="text-[12px]" style={ink(500)}>Bookings updated {updated}</div>}
      </section>
      )}
    </div>
  );
};

const AllBusinessesActivity: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const permissions = useUserPermissions();
  // The tenant Front Desk has no read of the bookings stats yet (403 manage_bookings, L1): until the server grants
  // it, Overview is not offered to it and nothing asks for those numbers. It opens on the bookings list.
  const hasOverview = !(permissions.isTenantFrontDesk && !permissions.isAdmin);
  // A link to a booking or to a business's bookings (/bookings?booking=&business=) opens the list.
  const view: View = params.get('view') === 'passes' ? 'passes'
    : params.get('view') === 'bookings' || params.get('booking') || params.get('business') || !hasOverview ? 'bookings' : 'overview';
  const setView = (v: View) => {
    const next = new URLSearchParams(params);
    next.delete('booking');
    next.delete('business');
    if (v === 'overview') next.delete('view'); else next.set('view', v);
    setParams(next, { replace: true });
  };
  const canSeePasses = permissions.isAdmin || permissions.isInvitationManager || permissions.isResidentialInvitationManager || permissions.isAllInvitationManager || permissions.isPropertyManager || worksAsFrontDesk(permissions);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const tab = (on: boolean): React.CSSProperties => ({
    padding: '10px 14px', fontSize: 14, fontWeight: on ? 600 : 500, color: on ? brand : 'var(--ink-500)', background: 'none',
    border: 'none', borderBottom: `3px solid ${on ? brand : 'transparent'}`, marginBottom: -1, cursor: 'pointer', textDecoration: 'none',
  });

  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: 'var(--surface)' }}>
      <div className="shrink-0 px-4 sm:px-8 pt-6 flex flex-col gap-4">
        <div>
          <h1 className="m-0 text-[22px] sm:text-[26px] font-bold" style={ink(900)}>Activity</h1>
          <div className="text-[13px] sm:text-[14px]" style={ink(500)}>{today}</div>
        </div>
        <div role="tablist" aria-label="Activity" className="flex gap-1" style={{ borderBottom: '1px solid var(--line)' }}>
          {hasOverview && <button type="button" role="tab" aria-selected={view === 'overview'} style={tab(view === 'overview')} onClick={() => setView('overview')}>Overview</button>}
          <button type="button" role="tab" aria-selected={view === 'bookings'} style={tab(view === 'bookings')} onClick={() => setView('bookings')}>Bookings</button>
          {canSeePasses && <button type="button" role="tab" aria-selected={view === 'passes'} style={tab(view === 'passes')} onClick={() => setView('passes')}>Passes</button>}
        </div>
      </div>
      {view === 'overview' ? (
        <div className="grow min-h-0 overflow-y-auto px-4 sm:px-8 pt-5 pb-14"><Overview /></div>
      ) : view === 'passes' ? (
        // The Passes list itself (the /invitations screen), inside Activity under its own header.
        <div className="grow min-h-0 mt-4"><TableContent><Invitations /></TableContent></div>
      ) : (
        <div className="grow min-h-0 mt-4"><Bookings embedded /></div>
      )}
    </div>
  );
};

/** /bookings: the tabs for people who see every business; the plain Bookings page for the rest. */
export const ActivityHome: React.FC = () => {
  const { seesAllBusinesses } = useUserPermissions();
  return seesAllBusinesses ? <AllBusinessesActivity /> : <Bookings />;
};

export default ActivityHome;
